import React, { useEffect, useState } from "react";
import { Icon } from "@blueprintjs/core";
import burgerImg from "./assets/Gruppe1Burger.png";
import { ScreenProps } from "../../SequenceController";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { DecoyEffect } from "../../../../translations/experiments/DecoyEffect";
import { colors, commonStyles } from "../../../../components/colors";

type FriesScreenClick = {
    choice: number;
    millisecondsSinceFirstRender: number;
};

type FriesScreenResult = {
    selections: Array<FriesScreenClick>;
    reason?: string;
};

export const DecoyFriesScreenInitialResult: FriesScreenResult = {
    selections: [],
    reason: ""
};

function FriesOptions({
                          selected,
                          setSelected,
                          txt
                      }: {
    selected: number | null,
    setSelected: (choice: number) => void,
    txt: any
}) {
    const options = [
        {
            value: 0,
            title: txt.optionSmall,
            price: txt.priceSmall
        },
        {
            value: 1,
            title: txt.optionLarge,
            price: txt.priceLarge
        }
    ];

    return (
        <div style={{
            width: '100%',
            display: "flex",
            gap: 24,
            flexDirection: window.innerWidth < 600 ? "column" : "row",
            justifyContent: "center",
            alignItems: "center"
        }}>
            {options.map((option) => (
                <div
                    key={option.value}
                    style={{
                        ...(selected === option.value
                            ? commonStyles.optionCard.selected
                            : commonStyles.optionCard.default),
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                    onClick={() => setSelected(option.value)}
                >
                    {selected === option.value && (
                        <div style={commonStyles.selectionCheck}>
                            <Icon icon="tick" size={16} />
                        </div>
                    )}
                    <h3 style={{
                        color: colors.brillantBlue,
                        marginBottom: 16,
                        fontSize: 20,
                        fontWeight: 600,
                        textAlign: "center"
                    }}>
                        {option.title}
                    </h3>
                    <p style={{
                        fontSize: 17,
                        fontWeight: 500,
                        margin: 0,
                        textAlign: "center"
                    }}>
                        {option.price}
                    </p>
                </div>
            ))}
        </div>
    );
}

export const DecoyFriesScreen = (props: ScreenProps) => {
    const [firstRenderTimestamp, setFirstRenderTimestamp] = useState(0);
    const [selected, setSelected] = useState<number | null>(null);

    // Reason state and validation
    const [reason, setReason] = useState("");
    const [reasonTouched, setReasonTouched] = useState(false);
    const [reasonError, setReasonError] = useState<string>("");

    const { setCanContinue, setResult } = props;
    const { language } = useLanguage();
    const txt = DecoyEffect[language].screens.fries1;

    useEffect(() => {
        setFirstRenderTimestamp(Date.now());
        setCanContinue(false);
        setResult({ ...DecoyFriesScreenInitialResult });
        setReason("");
        setReasonTouched(false);
        setReasonError("");
    }, [setCanContinue, setResult]);

    useEffect(() => {
        const reasonOk = reason.trim().length > 0;
        if (!reasonOk && reasonTouched) {
            setReasonError(
                txt.reasonRequired ||
                (language === "de" ? "(erforderlich)" : "(required)")
            );
        } else {
            setReasonError("");
        }
        const canContinue = selected !== null && reasonOk;
        setCanContinue(canContinue);
        if (canContinue && selected !== null) {
            setResult({
                selections: [{
                    choice: selected + 1,
                    millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp
                }],
                reason: reason.trim()
            });
        } else {
            setResult((prev: FriesScreenResult) => ({
                ...prev,
                selections: selected !== null
                    ? [{
                        choice: selected + 1,
                        millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp
                    }]
                    : [],
                reason
            }));
        }
    }, [selected, reason, reasonTouched, setCanContinue, setResult, firstRenderTimestamp, txt, language]);

    return (
        <div className="experiment-container" style={{
            maxWidth: 1250,
            margin: "0 auto",
            padding: "24px 20px 44px",
            backgroundColor: colors.backgroundLight
        }}>
            <div style={{
                background: colors.white,
                borderRadius: 14,
                boxShadow: "0 2px 12px rgba(0,0,0,0.09)",
                padding: "24px 40px 28px",
                marginBottom: 28
            }}>
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 24px 0",
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: "center"
                }}>
                    {txt.heading}
                </h2>
                <div style={{
                    fontSize: 17,
                    lineHeight: 1.7,
                    color: colors.textMedium,
                    textAlign: "justify",
                    textJustify: "inter-word"
                }}>
                    <p style={{ marginTop: 0, marginBottom: 20 }}>
                        {txt.context}
                    </p>
                </div>
                <div style={{
                    width: "100%",
                    maxWidth: 540,
                    margin: "0 auto 8px auto",
                    background: "#f8f9fa",
                    borderRadius: 12,
                    overflow: "hidden",
                    boxShadow: "0 6px 20px 0 rgba(0,0,0,0.08)"
                }}>
                    <img
                        src={burgerImg}
                        alt="Burger Wahl"
                        style={{
                            width: "100%",
                            height: "auto",
                            display: "block"
                        }}
                    />
                </div>
            </div>
            <div style={{ textAlign: "center", margin: "0 0 28px 0" }}>
                <span style={{
                    color: colors.brillantBlue,
                    fontSize: 22,
                    fontWeight: 600,
                    textAlign: "center"
                }}>{txt.question}</span>
            </div>
            <FriesOptions selected={selected} setSelected={setSelected} txt={txt} />
            {/* Reason field */}
            <div style={{ marginTop: 38, maxWidth: 670, marginLeft: "auto", marginRight: "auto" }}>
                <label style={{
                    display: "block",
                    fontWeight: 600,
                    color: colors.textDark,
                    marginBottom: 8
                }}>
                    {txt.reasonLabel ||
                        (language === "de" ? "Bitte begründe deine Wahl:" : "Please explain your choice:")}{" "}
                    <span style={{ fontWeight: 500, color: colors.textMedium }}>
                        {txt.reasonRequired || (language === "de" ? "(erforderlich)" : "(required)")}
                    </span>
                </label>
                <textarea
                    aria-label={txt.reasonLabel || (language === "de" ? "Begründung für die Wahl" : "Reason for choice")}
                    placeholder={txt.reasonPlaceholder || (language === "de" ? "Bitte gib eine kurze Begründung an." : "Please enter a brief explanation.")}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    onBlur={() => setReasonTouched(true)}
                    rows={4}
                    style={{
                        width: "100%",
                        maxWidth: 640,
                        minHeight: 96,
                        padding: 12,
                        fontSize: 15,
                        borderRadius: 8,
                        border: "1px solid #e1e6ea",
                        resize: "vertical",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.03)"
                    }}
                />
                {reasonError && <div style={{ color: "red", marginTop: 8 }}>{reasonError}</div>}
            </div>
        </div>
    );
};

export const DecoyFriesScreenConfig: ScreenConfig = {
    component: DecoyFriesScreen,
    defaultResult: DecoyFriesScreenInitialResult,
    canContinueDefault: false
};